import { useCallback, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Segmented } from "components/ios/Effects";
import { SplitView, useSplitClose, useSplitLink } from "components/ios/SplitView";
import { Tracker } from "components/access/Orders";
import DepositPanel from "components/admin/commerce/DepositPanel";
import { hasCapability, useAdmin } from "components/admin/RequireAdmin";
import {
  Badge,
  ActionForm,
  ErrorNotice,
  Loading,
  Record,
  useRemote,
} from "components/admin/workspace/Controls";
import { date } from "components/admin/workspace/Records";
import { RefundableLine, SyncLine } from "components/admin/workspace/Sync";
import { adminRequest, operationKey, queryString } from "lib/adminSiteApi";
import ManualDelivery from "./ManualDelivery";

const ORDERS = "/admin/commerce/orders";
const ARRANGEMENTS = { w2: "W-2 employees", "owner-operator": "Owner-operators", both: "W-2 employees and owner-operators" };

export function orderMoney(value, currency = "USD") {
  if (!Number.isSafeInteger(value) || value < 0) return "Not recorded";
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(value / 100);
}

export function depositMinorUnits(value) {
  if (!/^\d{1,9}(\.\d{1,2})?$/.test(value)) return null;
  const [whole, fraction = ""] = value.split(".");
  const amount = Number(whole) * 100 + Number(fraction.padEnd(2, "0"));
  return Number.isSafeInteger(amount) && amount > 0 ? amount : null;
}

// The list stays put (filter, page) while an order opens beside it.
export default function Orders({ id }) {
  const close = useSplitClose("/admin/orders");
  // Saving in the card (bank transfer verified, released) refreshes the list beside it.
  const [changed, setChanged] = useState(0);
  const onChanged = useCallback(() => setChanged((n) => n + 1), []);
  return (
    <SplitView
      open={Boolean(id)}
      list={<OrderList selectedId={id} changed={changed} />}
      detail={id ? <OrderDetail key={id} id={id} onChanged={onChanged} /> : null}
      detailKey={id}
      title={id ? `Order ${shortReference({ id })}` : "Order"}
      onClose={close}
      closeLabel="Close order"
      backLabel="Orders"
    />
  );
}

const QUICK = [
  { value: "all", label: "All", params: {} },
  { value: "transfer", label: "Awaiting bank transfer", params: { status: "awaiting_transfer" } },
  { value: "prepare", label: "Needs preparation", params: { fulfillmentStatus: "pending_preparation" } },
  { value: "review", label: "Payment review", params: { status: "review_required" } },
  { value: "released", label: "Released", params: { fulfillmentStatus: "released" } },
];
const REF = /^\s*MAG-([0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12})\s*$/i;
const shortReference = (order) => `MAG-${String(order.id || "").slice(0, 8).toUpperCase()}`;
const PAYMENT_WORDS = { awaiting_transfer: "Awaiting bank transfer", pending: "Payment started", paid: "Paid", review_required: "Payment review", payment_failed: "Payment failed", expired: "Expired", cancelled: "Cancelled" };
const PREP_WORDS = { awaiting_payment: "Waiting for payment", pending_preparation: "Needs preparation", released: "Released", access_granted: "Access granted", review_required: "On hold", cancelled: "Cancelled" };

function OrderList({ selectedId, changed = 0 }) {
  const nav = useNavigate(), link = useSplitLink(selectedId);
  const [cursor, setCursor] = useState(null);
  const [pages, setPages] = useState([]);
  const [quick, setQuick] = useState("all");
  const [reference, setReference] = useState(""), [refError, setRefError] = useState("");
  const params = QUICK.find((q) => q.value === quick).params;
  const state = useRemote(`${ORDERS}?${queryString({ limit: 25, after: cursor, ...params })}`, changed, { keep: true });
  const items = [...(state.data?.items || [])].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  const choose = (value) => { setQuick(value); setCursor(null); setPages([]); };
  return (
    <section className="aw-panel">
      <div className="aw-row">
        <h2>Website orders</h2>
        <form className="aw-goto" onSubmit={(e) => {
          e.preventDefault();
          const m = REF.exec(reference);
          if (!m) return setRefError("Paste the full reference, for example from a bank statement: MAG-7F3A9C2E-…");
          setRefError(""); nav(`/admin/orders/${m[1].toLowerCase()}`, { replace: link.replace, state: link.state });
        }}>
          <label className="aw-sr" htmlFor="aw-goto-ref">Go to order reference</label>
          <input id="aw-goto-ref" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Go to reference: MAG-…" autoComplete="off" spellCheck="false" />
          <button className="aw-button">Open</button>
        </form>
      </div>
      {refError && <p className="aw-notice aw-error" role="alert">{refError}</p>}
      <Segmented label="Show orders" items={QUICK} value={quick} onChange={choose} className="aw-orders-quick" />
      <Loading state={state}>
        {items.length ? (
          <div className="aw-table-wrap" role="region" tabIndex={0} aria-label="Orders table; scroll horizontally for more columns">
            <table>
              <caption className="aw-sr">Website orders, newest first on this page</caption>
              <thead><tr><th scope="col">Order</th><th scope="col" data-priority="2">Customer</th><th scope="col" data-priority="2">Total</th><th scope="col" data-priority="1">Payment</th><th scope="col" data-priority="3">Preparation</th><th scope="col" data-priority="3">Created</th></tr></thead>
              <tbody>{items.map((order) => (
                <tr key={order.id} data-split-row="" data-selected={order.id === selectedId ? "true" : undefined}>
                  <th scope="row"><Link className="aw-link" to={`/admin/orders/${encodeURIComponent(order.id)}`} title={order.reference || order.id}
                    aria-current={order.id === selectedId ? "true" : undefined} {...link}>{shortReference(order)}</Link>
                    <small>{order.kind === "course_enrollment" ? "Course" : (order.items || []).map((i) => [i.name, i.stateLabel].filter(Boolean).join(" · ")).join(", ")}</small></th>
                  <td data-priority="2">{order.customer?.name || "Not recorded"}<small>{order.customer?.company || order.customer?.email}</small></td>
                  <td data-priority="2">{orderMoney(order.totalMinor, order.currency)}<small>{order.provider === "swift" ? "Bank transfer" : order.provider === "stripe" ? "Card" : ""}</small></td>
                  <td data-priority="1"><Badge value={order.status} text={PAYMENT_WORDS[order.status]} /></td>
                  <td data-priority="3"><Badge value={order.fulfillmentStatus} text={PREP_WORDS[order.fulfillmentStatus]} /></td>
                  <td data-priority="3">{date(order.createdAt)}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        ) : <p className="aw-empty">{quick === "all" ? "No website orders yet." : "All clear — no orders in this view."}</p>}
      </Loading>
      <div className="aw-row aw-pagination">
        <button className="aw-button" disabled={state.loading || !pages.length} onClick={() => { setCursor(pages[pages.length - 1]); setPages(pages.slice(0, -1)); }}>Previous</button>
        <span>Page {pages.length + 1}</span>
        <button className="aw-button" disabled={state.loading || !!state.error || !state.data?.next} onClick={() => { setPages([...pages, cursor]); setCursor(state.data.next); }}>Next</button>
      </div>
    </section>
  );
}

function OrderDetail({ id, onChanged }) {
  const profile = useAdmin();
  const [refresh, setRefresh] = useState(0);
  const [receipt, setReceipt] = useState(null);
  const state = useRemote(`${ORDERS}/${encodeURIComponent(id)}`, refresh);
  const order = state.data?.order;
  const refreshOrder = (result) => {
    setReceipt(result?.requestId || null);
    setRefresh((value) => value + 1);
    onChanged?.();
  };
  const released = () => { setRefresh((v) => v + 1); onChanged?.(); };
  return (
    <>
      <div className="aw-row">
        <Link to="/admin/orders" className="aw-link aw-back">← Back to orders</Link>
        <button className="aw-button" onClick={() => setRefresh((value) => value + 1)}>Reload latest order</button>
      </div>
      {receipt && <p className="aw-notice" role="status">Bank transfer verification saved. Receipt: {receipt}</p>}
      <Loading state={state}>
        {order ? (
          <>
            <section className="aw-panel">
              <div className="aw-row"><h2 title={order.reference || order.id}>{shortReference(order)}</h2><Badge value={order.status} text={PAYMENT_WORDS[order.status]} /></div>
              <Tracker order={order} labels={order.kind === "course_enrollment" ? ["Ordered", "Payment confirmed", "Access granted"] : ["Ordered", "Payment confirmed", "Prepare files", "Released"]} />
              <Record data={{
                paymentMethod: order.provider === "swift" ? "SWIFT bank transfer" : order.provider === "stripe" ? "Stripe" : "Not recorded",
                total: orderMoney(order.totalMinor, order.currency),
                currency: order.currency,
                preparation: PREP_WORDS[order.fulfillmentStatus] || order.fulfillmentStatus,
                createdAt: order.createdAt,
                paidAt: order.paidAt,
              }} />
              {hasCapability(profile, "integrations.read") && <SyncLine type="order" id={order.id} />}
              {Number.isSafeInteger(order.paidAt) && <RefundableLine orderId={order.id} />}
              <p className="aw-muted">{order.kind === "course_enrollment" ? "Verified course payments grant access in the website customer account." : "Paid packages await preparation. Payment confirmation does not confirm delivery."}</p>
            </section>
            <NextStep order={order} profile={profile} onSaved={refreshOrder} onReleased={released} />
            <section className="aw-panel">
              <h2>Purchased packages</h2>
              <div className="aw-table-wrap" role="region" tabIndex={0} aria-label="Purchased packages; scroll horizontally for more columns">
                <table>
                  <caption className="aw-sr">Packages in this order</caption>
                  <thead><tr><th scope="col">Package</th><th scope="col">Working arrangement</th><th scope="col">Quantity</th><th scope="col">Unit price</th><th scope="col">Total</th></tr></thead>
                  <tbody>{(order.items || []).map((item, index) => (
                    <tr key={`${item.packageId}:${item.arrangement}:${item.equipment}:${item.state}:${index}`}>
                      <th scope="row">{item.name || item.packageId}</th>
                      <td>{item.type ? <>{item.type}<small>{item.accessDays} days · {(item.members || []).length} courses</small></> : <>{ARRANGEMENTS[item.arrangement] || item.arrangement}<small>Equipment: {item.equipmentLabel || item.equipment || "Not recorded"}</small><small>State edition: {item.stateLabel || item.state || "Not recorded"}</small></>}</td>
                      <td>{item.quantity}</td>
                      <td>{orderMoney(item.unitPriceMinor, order.currency)}</td>
                      <td>{orderMoney(item.lineTotalMinor, order.currency)}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </section>
            {order.kind === 'course_enrollment' && order.provider === 'swift' && order.status === 'awaiting_transfer' && hasCapability(profile,'commerce.reconcile') && <ActionForm title="Cancel pending course transfer" description="Cancel this unpaid order and release its reserved live seats. A later bank deposit must be reviewed separately; cancellation does not issue a refund."
              schema={{type:'object',properties:{reason:{type:'string',minLength:1,maxLength:1000},confirmed:{type:'boolean'}},required:['reason','confirmed']}} initial={{reason:'',confirmed:false}}
              submit={(body,key)=>adminRequest(`${ORDERS}/${id}/cancel-course`,{method:'POST',body,key})} onDone={refreshOrder}/>}
            <section className="aw-panel">
              <h2>Customer</h2>
              <Record data={{ name: order.customer?.name, email: order.customer?.email, phone: order.customer?.phone, company: order.customer?.company, dotNumber: order.customer?.dotNumber, mcNumber: order.customer?.mcNumber }} />
            </section>
            {(order.kind === "base_preparation" || order.depositOutcome === "basic") && !(order.status === "paid" && order.fulfillmentStatus === "pending_preparation") && (hasCapability(profile, "commerce.deliver")
              ? <ManualDelivery key={order.id} order={order} onReleased={released} />
              : <p className="aw-notice">An authorized delivery administrator must prepare and release these order files.</p>)}
          </>
        ) : !state.loading && !state.error ? <p className="aw-notice" role="alert">This order could not be found.</p> : null}
      </Loading>
    </>
  );
}

// One clear next step at the top of the order (approved iOS redesign). The review-before-confirm
// forms inside it are the existing ones, unchanged.
function NextStep({ order, profile, onSaved, onReleased }) {
  let title = null, body = null;
  if (order.provider === "swift" && order.status === "awaiting_transfer") {
    title = "Next step: match the bank transfer";
    body = hasCapability(profile, "commerce.reconcile")
      ? <SwiftVerification order={order} onSaved={onSaved} />
      : <p className="aw-notice">An authorized payment administrator must verify receipt of this bank transfer.</p>;
  } else if (order.kind === "deposit" && order.status === "paid" && !order.depositOutcome && order.fulfillmentStatus === "pending_review") {
    title = "Next step: the review meeting";
    body = hasCapability(profile, "commerce.deliver") ? <DepositPanel key={order.id} order={order} onSaved={onSaved} />
      : <p className="aw-notice">An authorized delivery administrator handles review deposits.</p>;
  } else if ((order.kind === "base_preparation" || order.depositOutcome === "basic") && order.status === "paid" && order.fulfillmentStatus === "pending_preparation") {
    title = "Next step: prepare and release the files";
    body = hasCapability(profile, "commerce.deliver")
      ? <ManualDelivery key={order.id} order={order} onReleased={onReleased} />
      : <p className="aw-notice">An authorized delivery administrator must prepare and release these order files.</p>;
  } else if (order.status === "review_required") {
    title = "Next step: payment review";
    body = <p className="aw-notice">{/dispute/.test(order.reviewReason || "") ? "A card dispute was opened. Access is paused and CarrierFlow access is revoked. Answer the dispute in Stripe." : /refund/.test(order.reviewReason || "") ? "A refund was recorded in Stripe. Access is paused and CarrierFlow access is revoked." : "This payment needs a review in Stripe before anything is delivered."}</p>;
  }
  if (!title) return null;
  return (
    <section className="aw-next-step" aria-labelledby="aw-next-step-title">
      <h2 id="aw-next-step-title" className="ios-group-header"><span>{title}</span></h2>
      {body}
    </section>
  );
}

function SwiftVerification({ order, onSaved }) {
  const [bankReference, setBankReference] = useState("");
  const [amount, setAmount] = useState("");
  const [verified, setVerified] = useState(false);
  const [review, setReview] = useState(null);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(false);
  const inFlight = useRef(false);
  const prepare = (event) => {
    event.preventDefault();
    const amountMinor = depositMinorUnits(amount);
    if (!bankReference.trim() || !verified || amountMinor !== order.totalMinor) {
      setError(new Error("Enter the bank transaction reference and the exact order amount, then confirm that you verified the cleared deposit."));
      return;
    }
    setError(null);
    setReview({ key: operationKey(), body: { bankReference: bankReference.trim(), amountMinor, currency: order.currency, verifiedDeposit: true } });
  };
  const confirm = async () => {
    if (!review || inFlight.current) return;
    inFlight.current = true;
    setPending(true);
    setError(null);
    try {
      const result = await adminRequest(`${ORDERS}/${encodeURIComponent(order.id)}/reconcile`, { method: "POST", key: review.key, body: review.body });
      onSaved(result);
    } catch (failure) {
      setError(failure);
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  };
  return (
    <section className="aw-panel" aria-labelledby="swift-verification-title">
      <h2 id="swift-verification-title">Verify received SWIFT payment</h2>
      <p className="aw-muted">Check your bank account for the cleared deposit and match it to this order. A payment instruction or customer transfer receipt is not confirmation that funds have arrived.</p>
      <ErrorNotice error={error} />
      {review ? (
        <div>
          <h3>Review before confirming</h3>
          <Record data={{ orderReference: order.reference || order.id, customer: order.customer?.name, bankReference: review.body.bankReference, receivedAmount: orderMoney(review.body.amountMinor, review.body.currency), currency: review.body.currency }} />
          <p className="aw-notice">{order.kind === "course_enrollment" ? "Confirming will mark this order paid and grant the purchased course access." : "Confirming will mark this order paid and awaiting package preparation."}</p>
          <div className="aw-row">
            <button className="aw-button" disabled={pending} onClick={() => { setReview(null); setError(null); }}>Edit verification</button>
            <button className="aw-button aw-primary" disabled={pending} onClick={confirm}>{pending ? "Confirming…" : "Confirm received payment"}</button>
          </div>
        </div>
      ) : (
        <form onSubmit={prepare}>
          <div className="aw-fields">
            <label htmlFor="swift-bank-reference">Bank transaction reference<input id="swift-bank-reference" value={bankReference} maxLength={200} required onChange={(event) => setBankReference(event.target.value)} /></label>
            <label htmlFor="swift-received-amount">Amount received ({order.currency})<input id="swift-received-amount" inputMode="decimal" value={amount} placeholder="0.00" required aria-describedby="swift-expected-amount" onChange={(event) => setAmount(event.target.value)} /><span id="swift-expected-amount">Must match {orderMoney(order.totalMinor, order.currency)}.</span></label>
          </div>
          <label className="aw-check" htmlFor="swift-verified"><input id="swift-verified" type="checkbox" checked={verified} required onChange={(event) => setVerified(event.target.checked)} />I verified the cleared deposit in the bank account and matched it to this order.</label>
          <button className="aw-button aw-primary" type="submit">Review bank transfer</button>
        </form>
      )}
    </section>
  );
}
